import { use, useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';

import Device from 'features/device/domain/models/device';
import { usePostTestEvent } from 'features/device/send-test-event/domain/queries/send-test-event-queries';
import { EventType } from '../domain/models/event-type';
import SendTestEventCommand, { EventPayload } from '../domain/models/send-test-event-command';

export interface ISendTestEventHook {
  isSendTestEventPopupOpen: boolean;
  selectedDevice: Device | undefined;
  openSendTestEventPopup: (device: Device) => void;
  closePopup: () => void;
  form: UseFormReturn<Partial<SendTestEventCommand>, any>;
  submitTestEvent: (eventType: EventType, eventPayload: EventPayload) => void;
  sendTestEventIsLoading: boolean;
}

const useSendTestEvent = (): ISendTestEventHook => {
  const { t } = useTranslation('sendTestEvent');
  const { setErrorMessage } = use(ErrorMessageProviderContext);

  const [isSendTestEventPopupOpen, setIsSendTestEventPopupOpen] = useState<boolean>(false);
  const [selectedDevice, setSelectedDevice] = useState<Device>();
  const {
    mutate: sendEvent,
    isPending: sendTestEventIsLoading,
    isSuccess: sendTestEventIsSuccess,
    isError: sendTestEventIsError,
    error: sendTestEventError,
  } = usePostTestEvent();

  const form = useForm({
    mode: 'onBlur',
    defaultValues: {},
  });

  useEffect(() => {
    form.reset({
      gatewayId: selectedDevice?.gatewayId,
      deviceId: selectedDevice?.id,
      eventType: EventType.Assistance,
      eventPayload: { message: t('defaultMessage'), location: '' },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice]);

  useEffect(() => {
    if (sendTestEventIsSuccess) {
      closePopup();
    }
  }, [sendTestEventIsSuccess]);

  useEffect(() => {
    if (sendTestEventIsError && sendTestEventError) {
      setErrorMessage(sendTestEventError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendTestEventIsError]);

  const submitTestEvent = (eventType: EventType, eventPayload: EventPayload) => {
    if (selectedDevice == null) {
      return;
    }
    const command: SendTestEventCommand = {
      eventType,
      eventPayload,
      deviceId: selectedDevice.id!,
    };
    sendEvent(command);
  };

  const openSendTestEventPopup = (device: Device) => {
    setSelectedDevice(device);
    setIsSendTestEventPopupOpen(true);
  };

  const closePopup = () => {
    setIsSendTestEventPopupOpen(false);
    setSelectedDevice(undefined);
  };
  return {
    isSendTestEventPopupOpen,
    selectedDevice,
    openSendTestEventPopup,
    closePopup,
    form,
    submitTestEvent,
    sendTestEventIsLoading,
  };
};

export default useSendTestEvent;
